'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface IncidentInputs {
  // Impact
  usersAffected: 'none' | 'few' | 'some' | 'many' | 'all';
  functionalityImpact: 'none' | 'minor' | 'degraded' | 'major' | 'complete';

  // Business
  revenueImpact: 'none' | 'minimal' | 'moderate' | 'significant' | 'severe';
  reputationRisk: 'none' | 'low' | 'medium' | 'high' | 'critical';

  // Technical
  dataLoss: boolean;
  securityBreach: boolean;
  systemDown: boolean;

  // Operational
  workaroundAvailable: boolean;
  automatedAlerting: boolean;
}

interface SeverityClassification {
  level: 'SEV1' | 'SEV2' | 'SEV3' | 'SEV4';
  name: string;
  color: string;
  emoji: string;
  description: string;
  responseTime: string;
  responseTeam: string[];
  communicationPlan: string[];
  escalationPath: string[];
  postmortemRequired: boolean;
  exampleActions: string[];
}

export default function IncidentSeverityClassifierPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'incident-severity-classifier',
    toolCategory: 'analyzer',
  });

  const [inputs, setInputs] = useState<IncidentInputs>({
    usersAffected: 'some',
    functionalityImpact: 'degraded',
    revenueImpact: 'moderate',
    reputationRisk: 'medium',
    dataLoss: false,
    securityBreach: false,
    systemDown: false,
    workaroundAvailable: true,
    automatedAlerting: true,
  });

  const updateInput = <K extends keyof IncidentInputs>(key: K, value: IncidentInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const classification = useMemo((): SeverityClassification => {
    const {
      usersAffected,
      functionalityImpact,
      revenueImpact,
      reputationRisk,
      dataLoss,
      securityBreach,
      systemDown,
      workaroundAvailable,
    } = inputs;

    // Auto-escalate to SEV1 for critical situations
    if (securityBreach || dataLoss) {
      return {
        level: 'SEV1',
        name: 'Critical',
        color: 'border-red-500 bg-red-500/10',
        emoji: '🔴',
        description: 'Service completely down, security breach, or data loss. Immediate action required.',
        responseTime: 'Immediate (within 15 minutes)',
        responseTeam: ['On-call Engineer', 'Engineering Manager', 'CTO', 'Security Team (if applicable)'],
        communicationPlan: [
          'Page on-call engineer immediately',
          'Create war room / incident channel',
          'Notify leadership within 15 minutes',
          'Update status page immediately',
          'Prepare customer communication within 30 minutes',
          'Hourly updates to stakeholders',
        ],
        escalationPath: [
          '0-15 min: On-call engineer',
          '15-30 min: Engineering Manager + Senior Engineers',
          '30-60 min: CTO + Product Leadership',
          '60+ min: CEO + Executive Team',
        ],
        postmortemRequired: true,
        exampleActions: [
          'Declare major incident',
          'Assemble incident response team',
          'Create dedicated incident channel',
          'Begin root cause analysis',
          'Implement immediate mitigation',
          'Notify all stakeholders',
          'Update status page',
          'Prepare customer communications',
        ],
      };
    }

    // SEV1: Complete outage or all users affected
    if (systemDown || usersAffected === 'all' || functionalityImpact === 'complete') {
      return {
        level: 'SEV1',
        name: 'Critical',
        color: 'border-red-500 bg-red-500/10',
        emoji: '🔴',
        description: 'Service completely down or all users unable to access core functionality.',
        responseTime: 'Immediate (within 15 minutes)',
        responseTeam: ['On-call Engineer', 'Engineering Manager', 'CTO'],
        communicationPlan: [
          'Page on-call engineer immediately',
          'Create war room / incident channel',
          'Notify leadership within 15 minutes',
          'Update status page immediately',
          'Prepare customer communication within 30 minutes',
          'Hourly updates to stakeholders',
        ],
        escalationPath: [
          '0-15 min: On-call engineer',
          '15-30 min: Engineering Manager + Senior Engineers',
          '30-60 min: CTO + Product Leadership',
          '60+ min: CEO + Executive Team',
        ],
        postmortemRequired: true,
        exampleActions: [
          'Declare major incident',
          'Assemble incident response team',
          'Roll back recent changes if applicable',
          'Scale up resources if needed',
          'Implement emergency fixes',
          'Coordinate with infrastructure team',
        ],
      };
    }

    // SEV2: Major degradation or many users affected
    if (
      usersAffected === 'many' ||
      functionalityImpact === 'major' ||
      revenueImpact === 'significant' ||
      revenueImpact === 'severe' ||
      reputationRisk === 'critical'
    ) {
      return {
        level: 'SEV2',
        name: 'High',
        color: 'border-accent bg-accent-muted',
        emoji: '🟠',
        description: 'Major functionality degraded or significant user impact. Urgent attention required.',
        responseTime: 'Within 30 minutes',
        responseTeam: ['On-call Engineer', 'Engineering Manager', 'Product Manager'],
        communicationPlan: [
          'Alert on-call engineer',
          'Create incident channel',
          'Notify engineering leadership within 30 minutes',
          'Update status page if customer-facing',
          'Prepare internal updates',
          'Regular updates every 2-4 hours',
        ],
        escalationPath: [
          '0-30 min: On-call engineer',
          '30-60 min: Engineering Manager',
          '1-2 hours: CTO if not resolved',
          '2+ hours: Product/Executive leadership',
        ],
        postmortemRequired: true,
        exampleActions: [
          'Start investigation immediately',
          'Gather relevant logs and metrics',
          'Implement temporary workarounds',
          'Engage additional engineers if needed',
          'Monitor impact metrics closely',
          'Document timeline and actions',
        ],
      };
    }

    // SEV3: Moderate impact
    if (
      usersAffected === 'some' ||
      functionalityImpact === 'degraded' ||
      revenueImpact === 'moderate' ||
      reputationRisk === 'high' ||
      !workaroundAvailable
    ) {
      return {
        level: 'SEV3',
        name: 'Medium',
        color: 'border-yellow-500 bg-yellow-500/10',
        emoji: '🟡',
        description: 'Functionality degraded for subset of users. Should be addressed same day.',
        responseTime: 'Within 2 hours',
        responseTeam: ['On-call Engineer', 'Team Lead'],
        communicationPlan: [
          'Notify on-call engineer',
          'Create ticket or incident thread',
          'Update team in daily standup',
          'Notify affected customers if applicable',
          'Daily status updates',
        ],
        escalationPath: [
          '0-2 hours: On-call engineer',
          '2-4 hours: Team Lead',
          '4-8 hours: Engineering Manager',
          'EOD: Review if not resolved',
        ],
        postmortemRequired: false,
        exampleActions: [
          'Begin investigation during business hours',
          'Review recent deployments',
          'Check monitoring and logs',
          'Implement fix in next deployment',
          'Add monitoring if gaps identified',
          'Update documentation',
        ],
      };
    }

    // SEV4: Minor impact
    return {
      level: 'SEV4',
      name: 'Low',
      color: 'border-border bg-background/50',
      emoji: '⚪',
      description: 'Minor issues with minimal user impact. Can be addressed in normal workflow.',
      responseTime: 'Within 1 business day',
      responseTeam: ['Team Member', 'On-call Engineer (if urgent)'],
      communicationPlan: [
        'Create ticket in issue tracker',
        'Assign to appropriate team',
        'Include in sprint planning',
        'No immediate stakeholder notification needed',
      ],
      escalationPath: [
        '0-24 hours: Assigned engineer',
        '1-3 days: Team Lead review',
        '3-7 days: Include in sprint planning',
      ],
      postmortemRequired: false,
      exampleActions: [
        'Create detailed bug report',
        'Add to backlog',
        'Prioritize in next sprint',
        'Fix in regular development cycle',
        'Test thoroughly before deployment',
      ],
    };
  }, [inputs]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    // Create gaps based on critical factors
    const gaps = [];
    if (inputs.securityBreach) {
      gaps.push({
        id: 'security-breach',
        title: 'Security Breach Detected',
        severity: 'critical' as const,
        description: 'Active security breach requiring immediate response',
        recommendation: 'Engage security team and follow incident response protocol',
      });
    }
    if (inputs.dataLoss) {
      gaps.push({
        id: 'data-loss',
        title: 'Data Loss or Corruption',
        severity: 'critical' as const,
        description: 'Data integrity issue detected',
        recommendation: 'Initiate data recovery procedures and assess impact scope',
      });
    }
    if (inputs.systemDown) {
      gaps.push({
        id: 'system-down',
        title: 'Complete System Outage',
        severity: 'critical' as const,
        description: 'Service completely unavailable',
        recommendation: 'Declare major incident and assemble response team',
      });
    }

    // Convert communication plan to recommendations
    const recommendations = classification.communicationPlan.map((step, idx) => ({
      id: `comm-step-${idx}`,
      title: step,
      priority: idx < 3 ? 'high' as const : 'medium' as const,
      description: step,
      effort: 'Low',
    }));

    // Convert severity to score (SEV1=25, SEV2=50, SEV3=75, SEV4=100 - inverted for "health")
    const severityScore = classification.level === 'SEV1' ? 25 :
                          classification.level === 'SEV2' ? 50 :
                          classification.level === 'SEV3' ? 75 : 100;

    const assessment = await saveResult({
      name: `Incident Classification - ${classification.level} - ${new Date().toLocaleDateString()}`,
      score_overall: severityScore,
      scores: {
        severityLevel: classification.level === 'SEV1' ? 1 : classification.level === 'SEV2' ? 2 : classification.level === 'SEV3' ? 3 : 4,
      },
      answers: inputs as unknown as Record<string, unknown>,
      gaps,
      recommendations,
      metadata: {
        severityLevel: classification.level,
        severityName: classification.name,
        responseTime: classification.responseTime,
        responseTeam: classification.responseTeam,
        escalationPath: classification.escalationPath,
        postmortemRequired: classification.postmortemRequired,
        exampleActions: classification.exampleActions,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const severityDefinitions = [
    {
      level: 'SEV1',
      name: 'Critical',
      emoji: '🔴',
      examples: [
        'Complete service outage',
        'Data loss or corruption',
        'Security breach',
        'All users unable to access core features',
        'Payment processing completely down',
      ],
    },
    {
      level: 'SEV2',
      name: 'High',
      emoji: '🟠',
      examples: [
        'Major feature not working for many users',
        'Significant performance degradation',
        'Revenue-generating feature impaired',
        'Authentication issues affecting large percentage',
        'Critical API endpoints failing',
      ],
    },
    {
      level: 'SEV3',
      name: 'Medium',
      emoji: '🟡',
      examples: [
        'Feature degraded for subset of users',
        'Non-critical functionality impaired',
        'Workaround available but inconvenient',
        'Minor performance issues',
        'UI bugs affecting usability',
      ],
    },
    {
      level: 'SEV4',
      name: 'Low',
      emoji: '⚪',
      examples: [
        'Cosmetic UI issues',
        'Minor bugs with minimal impact',
        'Edge case failures',
        'Documentation errors',
        'Non-urgent improvements',
      ],
    },
  ];

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🎯</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              Incident Severity Classifier
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Determine incident severity levels and appropriate response procedures
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">
            Tools
          </Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Incident Severity Classifier</span>
        </nav>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this classifier</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Answer questions about the incident&apos;s impact</li>
            <li>Review the automatically determined severity level</li>
            <li>Follow the response procedures for that severity</li>
            <li>Use the communication plan and escalation path provided</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Inputs */}
          <div className="space-y-6">
            {/* User Impact */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">User Impact</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  How many users are affected?
                </label>
                <select
                  value={inputs.usersAffected}
                  onChange={(e) => updateInput('usersAffected', e.target.value as IncidentInputs['usersAffected'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="none">No users (internal only)</option>
                  <option value="few">Few users (&lt;1%)</option>
                  <option value="some">Some users (1-10%)</option>
                  <option value="many">Many users (10-50%)</option>
                  <option value="all">All users (&gt;50%)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  What is the functionality impact?
                </label>
                <select
                  value={inputs.functionalityImpact}
                  onChange={(e) => updateInput('functionalityImpact', e.target.value as IncidentInputs['functionalityImpact'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="none">No impact</option>
                  <option value="minor">Minor feature affected</option>
                  <option value="degraded">Core feature degraded</option>
                  <option value="major">Major feature unavailable</option>
                  <option value="complete">Complete service outage</option>
                </select>
              </div>
            </div>

            {/* Business Impact */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Business Impact</h3>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Revenue impact?
                </label>
                <select
                  value={inputs.revenueImpact}
                  onChange={(e) => updateInput('revenueImpact', e.target.value as IncidentInputs['revenueImpact'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="none">No revenue impact</option>
                  <option value="minimal">Minimal (&lt;$1k/hour)</option>
                  <option value="moderate">Moderate ($1k-$10k/hour)</option>
                  <option value="significant">Significant ($10k-$100k/hour)</option>
                  <option value="severe">Severe (&gt;$100k/hour)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Reputation risk?
                </label>
                <select
                  value={inputs.reputationRisk}
                  onChange={(e) => updateInput('reputationRisk', e.target.value as IncidentInputs['reputationRisk'])}
                  className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
                >
                  <option value="none">No risk</option>
                  <option value="low">Low (minor inconvenience)</option>
                  <option value="medium">Medium (customer frustration)</option>
                  <option value="high">High (potential churn)</option>
                  <option value="critical">Critical (major PR issue)</option>
                </select>
              </div>
            </div>

            {/* Technical Factors */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Technical Factors</h3>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.dataLoss}
                    onChange={(e) => updateInput('dataLoss', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <div>
                    <div className="text-foreground font-semibold">Data Loss or Corruption</div>
                    <div className="text-xs text-foreground-tertiary">Critical: Auto-escalates to SEV1</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.securityBreach}
                    onChange={(e) => updateInput('securityBreach', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <div>
                    <div className="text-foreground font-semibold">Security Breach or Vulnerability</div>
                    <div className="text-xs text-foreground-tertiary">Critical: Auto-escalates to SEV1</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.systemDown}
                    onChange={(e) => updateInput('systemDown', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <div>
                    <div className="text-foreground font-semibold">Complete System Down</div>
                    <div className="text-xs text-foreground-tertiary">Service completely unavailable</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Operational Factors */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Operational Factors</h3>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.workaroundAvailable}
                    onChange={(e) => updateInput('workaroundAvailable', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <div>
                    <div className="text-foreground font-semibold">Workaround Available</div>
                    <div className="text-xs text-foreground-tertiary">Users can accomplish task another way</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.automatedAlerting}
                    onChange={(e) => updateInput('automatedAlerting', e.target.checked)}
                    className="w-5 h-5"
                  />
                  <div>
                    <div className="text-foreground font-semibold">Automated Alerting Exists</div>
                    <div className="text-xs text-foreground-tertiary">System automatically detected issue</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column: Classification Results */}
          <div className="space-y-6">
            {/* Severity Level */}
            <div className={`bg-surface rounded-lg border ${classification.color} p-6`}>
              <h3 className="text-xl font-bold text-foreground mb-4">Severity Classification</h3>

              <div className="text-center py-6 mb-4">
                <div className="text-6xl mb-4">{classification.emoji}</div>
                <div className="text-4xl font-bold text-foreground mb-2">{classification.level}</div>
                <div className="text-xl text-accent mb-4">{classification.name}</div>
                <p className="text-foreground-secondary">{classification.description}</p>
              </div>

              <div className="bg-background rounded p-4">
                <div className="text-sm font-semibold text-foreground mb-2">Response Time</div>
                <div className="text-accent font-bold">{classification.responseTime}</div>
              </div>
            </div>

            {/* Response Team */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Response Team</h3>
              <ul className="space-y-2">
                {classification.responseTeam.map((member, i) => (
                  <li key={i} className="flex items-center gap-2 text-foreground-secondary">
                    <span className="text-accent">•</span>
                    {member}
                  </li>
                ))}
              </ul>
            </div>

            {/* Communication Plan */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Communication Plan</h3>
              <ol className="space-y-2 list-decimal list-inside">
                {classification.communicationPlan.map((step, i) => (
                  <li key={i} className="text-foreground-secondary text-sm">{step}</li>
                ))}
              </ol>
            </div>

            {/* Escalation Path */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Escalation Path</h3>
              <div className="space-y-2">
                {classification.escalationPath.map((step, i) => (
                  <div key={i} className="p-3 bg-background rounded border border-border-muted">
                    <div className="text-foreground-secondary text-sm">{step}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Actions */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Immediate Actions</h3>
              <ul className="space-y-2">
                {classification.exampleActions.map((action, i) => (
                  <li key={i} className="flex items-start gap-2 text-foreground-secondary text-sm">
                    <span className="text-accent mt-1">→</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>

            {/* Postmortem */}
            <div className={`rounded-lg border p-4 ${classification.postmortemRequired ? 'bg-accent-muted border-border' : 'bg-surface border-border'}`}>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{classification.postmortemRequired ? '📝' : 'ℹ️'}</span>
                <div>
                  <div className="font-semibold text-foreground">
                    Postmortem {classification.postmortemRequired ? 'Required' : 'Optional'}
                  </div>
                  <div className="text-sm text-foreground-secondary">
                    {classification.postmortemRequired
                      ? 'Schedule blameless postmortem within 48 hours'
                      : 'Document lessons learned in ticket'}
                  </div>
                </div>
              </div>
            </div>

            {/* Save to Command Center */}
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
            {saveError && (
              <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                {saveError}
              </div>
            )}
          </div>
        </div>

        {/* Severity Reference Guide */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Severity Level Reference</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {severityDefinitions.map((severity) => (
              <div key={severity.level} className="border border-border-muted rounded p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{severity.emoji}</span>
                  <div>
                    <div className="font-bold text-foreground">{severity.level}</div>
                    <div className="text-sm text-foreground-tertiary">{severity.name}</div>
                  </div>
                </div>
                <div className="text-xs text-foreground-secondary space-y-1">
                  <div className="font-semibold text-foreground mb-2">Examples:</div>
                  {severity.examples.map((example, i) => (
                    <div key={i}>• {example}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-3">Related Resources</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              href="/frameworks"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Incident Response Frameworks →</h4>
              <p className="text-sm text-foreground-tertiary">
                Best practices for managing production incidents
              </p>
            </Link>
            <Link
              href="/templates"
              className="p-4 bg-background rounded border border-border-muted hover:border-border transition-colors"
            >
              <h4 className="font-bold text-foreground mb-1">Postmortem Templates →</h4>
              <p className="text-sm text-foreground-tertiary">
                Structured templates for blameless postmortems
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
